import { Suspense, useEffect, useMemo, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconLink } from '@iwoca/icons';
import { Spinner } from '@iwoca/orion';
import { useNavigate, useParams } from 'react-router';

import styles from './ShareLinkOpenBanking.module.css';
import { useGetOpenBankingActiveAccounts } from '../../api/lendingApi/lendingApiQueries';
import { useGetOpenBankingProviders } from '../../api/openBankingShareableLinkApi/useGetOpenBankingProviders';
import { usePostOpenBankingShareableLinkMutation } from '../../api/openBankingShareableLinkApi/usePostOpenBankingShareableLinkMutation';
import { useGetOpenBankingShareableLink } from '../../api/openBankingShareableLinkApi/useQueryOpenShareableLink';
import { Button } from '../Button/Button';
import { BankSelectionModal } from '../TradingHistory/OpenBanking/BankSelectionModal/BankSelectionModal';
import { TrueLayerDocumentRequirementContentUK } from '../TradingHistory/OpenBanking/LinkOpenBankingDocumentRequirementContent/TrueLayerDocumentRequirementContentUK';
import { TrueLayerLinkedAccountsList } from '../TradingHistory/OpenBanking/TrueLayerLinkedAccountsList/TrueLayerLinkedAccountsList';

export const ShareLinkOpenBanking = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    openBankingLink,
    isLoading: isShareableLinkLoading,
    isError: isShareableLinkError,
    error: shareableLinkError,
  } = useGetOpenBankingShareableLink({ token });
  const stateKey = openBankingLink?.state_key;

  useEffect(() => {
    if (!isShareableLinkLoading && isShareableLinkError) {
      const isExpired = shareableLinkError?.statusCode === 403;
      if (isExpired) {
        navigate(`/link-open-banking/${token}/expired`);
      } else {
        navigate(`/link-open-banking/${token}/error`);
      }
    }
  }, [
    isShareableLinkLoading,
    isShareableLinkError,
    shareableLinkError,
    token,
    navigate,
  ]);

  const { bankList, isLoading: isProvidersLoading } =
    useGetOpenBankingProviders({
      stateKey,
      token,
    });

  const { notifyApplicant, isPending } =
    usePostOpenBankingShareableLinkMutation();
  const {
    activeAccounts,
    isError: isActiveAccountsError,
    isLoading: isActiveAccountsLoading,
  } = useGetOpenBankingActiveAccounts({
    stateKey,
    token,
  });

  const isBankListReady =
    !isShareableLinkLoading &&
    !isProvidersLoading &&
    !isActiveAccountsLoading &&
    stateKey;

  const businessName = openBankingLink?.business_name;

  const handleLinkBankAccount = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleFinishAndNotify = async () => {
    if (token) {
      tracking.ampli.ctaClick({
        URL: window.location.pathname,
        'CTA text': 'Finish and notify applicant',
      });

      await notifyApplicant({ token });
      navigate(`/link-open-banking/${token}/success`);
    }
  };
  const linkedBankAccounts = useMemo(() => {
    if (!activeAccounts) return;

    return activeAccounts.accounts.flatMap((account) => {
      const matchingBank = bankList.find(
        (bank) => bank.bank_id === account.provider_id,
      );

      if (!matchingBank) return [];

      return [
        {
          bank_name: matchingBank.bank_name,
          logo_url: matchingBank.logo_url,
          account_number_masked: account.account_number_masked,
          provider_id: account.provider_id,
        },
      ];
    });
  }, [activeAccounts, bankList]);

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <h1 className={styles.title}>
          {businessName} needs your help to finish their loan application.
        </h1>
        <h2 className={styles.subTitle}>
          You have been requested to link their business bank account via our
          secure platform.
        </h2>
      </div>

      <p className={styles.iwocaIntro} data-testid="iwoca-intro">
        We are <a href="/">iwoca</a>, a business lender. We require the steps
        below to be completed in order to assess their suitability for credit.
      </p>

      {!isBankListReady && <Spinner dataTestId="share-link-loader" />}

      {isBankListReady && !linkedBankAccounts?.length && (
        <>
          <p className={styles.description}>
            <strong>
              Link {businessName}’s business bank account using Open Banking
            </strong>
          </p>
          <div className={styles.truelayerRequirement}>
            <TrueLayerDocumentRequirementContentUK />
          </div>
        </>
      )}

      <Suspense fallback={<Spinner dataTestId="linked-accounts-loader" />}>
        {isBankListReady &&
          linkedBankAccounts?.length &&
          !isActiveAccountsError && (
            <div data-testid="linked-accounts-list">
              <TrueLayerLinkedAccountsList
                linkedBankAccounts={linkedBankAccounts}
              />
            </div>
          )}
        {isBankListReady &&
          (linkedBankAccounts?.length ? (
            <div className={styles.additionalAccountLinkContainer}>
              <button
                className={styles.additionalAccountLink}
                onClick={handleLinkBankAccount}
              >
                Link an additional account +
              </button>
            </div>
          ) : (
            <div>
              <Button className={styles.button} onClick={handleLinkBankAccount}>
                Link bank account <IconLink />
              </Button>
            </div>
          ))}
      </Suspense>
      <BankSelectionModal
        isOpen={isModalOpen}
        handleClose={handleCloseModal}
        bankList={bankList}
        shouldSkipPermissionView={false}
        stateKey={stateKey || ''}
        ableToShareLink={false}
        shareableLinkToken={token}
        sharedLinkBusinessName={businessName}
      />

      {activeAccounts?.accounts && activeAccounts.accounts.length > 0 && (
        <div className={styles.finishSection}>
          <p className={styles.finishText}>
            All done and don’t need to link any more accounts?
          </p>
          <Button
            className={styles.finishButton}
            onClick={handleFinishAndNotify}
            disabled={isPending}
          >
            Finish and notify applicant
          </Button>
        </div>
      )}
    </div>
  );
};
